import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, X } from "lucide-react";
import api from "../../lib/api";
import {
  GITHUB_STATE_LABELS, QUALIFICATION_FIELDS, SOURCE_LABELS, STATUS_LABELS, TYPE_LABELS, productErrorMessage,
} from "../../lib/product";
import { SelectField } from "../tickets/TicketDialogs";
import { ItemPicker, TicketPicker } from "./ProductPickers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const NONE = "__none__";

export function useRequest() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onDone) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      onDone(data);
    } catch (e) {
      setError(productErrorMessage(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

export function Footer({ busy, error, onCancel, onSubmit, disabled, label, danger, testid }) {
  return (
    <>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="product-dialog-error">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Annuler</Button>
        <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={busy || disabled} data-testid={testid}>
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

// The accounts that can be named responsible: those who can read the product data (the server applies the same rule).
export function usePeople(enabled = true, path = "/product/people") {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get(path).then(({ data }) => setPeople(data.people)).catch(() => setPeople([]));
  }, [enabled, path]);
  return people;
}

export function OwnerSelect({ id, label, value, onChange, people, testid }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
        <SelectTrigger id={id} data-testid={testid || id}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Personne</SelectItem>
          {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// --- Status (D-12): a reason for a decision or a step back, a responsible to plan --------------------------------------------------
export function StatusDialog({ item, move, onOpenChange, onDone }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  const [owner, setOwner] = useState("");
  const people = usePeople(!!move?.asks?.includes("owner"));
  useEffect(() => { if (move) { setReason(""); setOwner(""); req.setError(""); } }, [move]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item || !move) return null;
  const asksReason = move.asks.includes("reason");
  const asksOwner = move.asks.includes("owner");
  const valid = (!asksReason || reason.trim().length >= 3) && (!asksOwner || !!owner) && !move.needs_release;
  const submit = () => {
    const body = { expected_version: item.version, status: move.status };
    if (asksReason) body.reason = reason.trim();
    if (asksOwner) body.owner_id = owner;
    req.run(() => api.post(`/product/items/${item.id}/status`, body), onDone);
  };
  const decision = ["retained", "abandoned"].includes(move.status) && !move.backward;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{move.label}</DialogTitle>
          <DialogDescription>{item.number} · {STATUS_LABELS[item.status]} → {STATUS_LABELS[move.status]}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {asksOwner && <OwnerSelect id="product-move-owner" label="Responsable *" value={owner} onChange={setOwner} people={people} />}
          {asksReason && (
            <div className="space-y-1.5">
              <Label htmlFor="product-move-reason">{decision ? "Motif de la décision *" : "Motif *"}</Label>
              <Textarea id="product-move-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="product-move-reason"
                placeholder={decision ? "Pourquoi cette décision ?" : "Pourquoi revenir en arrière ?"} />
            </div>
          )}
          {move.needs_release && (
            <p className="text-sm text-amber-800" data-testid="product-move-needs-release">
              Il faut d'abord une version livrée, avec sa preuve de déploiement.{" "}
              <Link to="/produit/versions" className="font-medium underline">Voir les versions</Link>
            </p>
          )}
          {!asksReason && !asksOwner && !move.needs_release && <p className="text-sm text-slate-600">Confirmer ce changement d'état ?</p>}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid} danger={move.status === "abandoned"}
          label={move.label} testid="product-move-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Qualification (D-13): six closed choices chosen by the person, never a computed score -----------------------------------------
export function QualifyDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [values, setValues] = useState({});
  useEffect(() => {
    if (open) { setValues(Object.fromEntries(QUALIFICATION_FIELDS.map((f) => [f.id, item?.[f.id] || ""]))); req.setError(""); }
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const changed = Object.fromEntries(QUALIFICATION_FIELDS.filter((f) => (values[f.id] || "") !== (item[f.id] || "")).map((f) => [f.id, values[f.id] || null]));
  const submit = () => req.run(() => api.post(`/product/items/${item.id}/qualify`, { expected_version: item.version, ...changed }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Qualifier</DialogTitle>
          <DialogDescription>Ce sont vos choix : rien n'est calculé à votre place.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {QUALIFICATION_FIELDS.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <Label htmlFor={`product-q-${f.id}`}>{f.label}</Label>
              <Select value={values[f.id] || NONE} onValueChange={(v) => setValues((s) => ({ ...s, [f.id]: v === NONE ? "" : v }))}>
                <SelectTrigger id={`product-q-${f.id}`} data-testid={`product-q-${f.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Non renseigné</SelectItem>
                  {Object.entries(f.options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={Object.keys(changed).length === 0}
          label="Enregistrer" testid="product-qualify-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Planning (manage_roadmap): theme, target version, responsible, dependencies -------------------------------------------------
export function PlanningDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const people = usePeople(open);
  const [theme, setTheme] = useState("");
  const [version, setVersion] = useState("");
  const [owner, setOwner] = useState("");
  const [deps, setDeps] = useState([]);
  useEffect(() => {
    if (!open || !item) return;
    setTheme(item.theme || ""); setVersion(item.target_version || ""); setOwner(item.owner_id || "");
    setDeps((item.dependencies || []).map((d) => ({ id: d.id, number: d.number, title: d.title })));
    req.setError("");
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const changes = {};
  if (theme.trim() !== (item.theme || "")) changes.theme = theme.trim() || null;
  if (version.trim() !== (item.target_version || "")) changes.target_version = version.trim() || null;
  if (owner !== (item.owner_id || "")) changes.owner_id = owner || null;
  const before = (item.depends_on || []).join(",");
  if (deps.map((d) => d.id).join(",") !== before) changes.depends_on = deps.map((d) => d.id);
  const submit = () => req.run(() => api.patch(`/product/items/${item.id}`, { expected_version: item.version, ...changes }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Planification</DialogTitle>
          <DialogDescription>Thème, version visée, responsable et ce dont cet élément dépend.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="product-plan-theme">Thème</Label>
              <Input id="product-plan-theme" value={theme} onChange={(e) => setTheme(e.target.value)} maxLength={60} placeholder="Caisse, Stock, Mobile…" data-testid="product-plan-theme" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-plan-version">Version cible</Label>
              <Input id="product-plan-version" value={version} onChange={(e) => setVersion(e.target.value)} maxLength={40} placeholder="2026.11" data-testid="product-plan-version" />
            </div>
          </div>
          <OwnerSelect id="product-plan-owner" label="Responsable" value={owner} onChange={setOwner} people={people} />
          <div className="space-y-2">
            <Label>Dépend de</Label>
            {deps.length > 0 && (
              <ul className="space-y-1" data-testid="product-plan-deps">
                {deps.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-sm">
                    <span className="min-w-0 break-words"><span className="text-xs text-slate-500 tabular-nums">{d.number}</span> {d.title}</span>
                    <button type="button" aria-label={`Retirer ${d.number}`} onClick={() => setDeps((l) => l.filter((x) => x.id !== d.id))}><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            {deps.length < 10 && (
              <ItemPicker excludeIds={[item.id, ...deps.map((d) => d.id)]} onPick={(i) => setDeps((l) => [...l, { id: i.id, number: i.number, title: i.title }])} testid="product-dep-picker" />
            )}
          </div>
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={Object.keys(changes).length === 0}
          label="Enregistrer" testid="product-plan-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Description (triage) ---------------------------------------------------------------------------------------------------------
export function EditDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (open && item) {
      setDraft({ title: item.title, description: item.description || "", type: item.type, source: item.source, estimate: item.pharmacies_estimate ?? "" });
      req.setError("");
    }
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const changes = {};
  if (draft.title?.trim() !== item.title) changes.title = draft.title?.trim();
  if ((draft.description || "").trim() !== (item.description || "")) changes.description = (draft.description || "").trim();
  if (draft.type !== item.type) changes.type = draft.type;
  if (draft.source !== item.source) changes.source = draft.source;
  const estimate = draft.estimate === "" || draft.estimate == null ? null : Number(draft.estimate);
  if (estimate !== (item.pharmacies_estimate ?? null)) changes.pharmacies_estimate = estimate;
  const valid = (draft.title || "").trim().length >= 3 && (estimate === null || (Number.isInteger(estimate) && estimate >= 0));
  const submit = () => req.run(() => api.patch(`/product/items/${item.id}`, { expected_version: item.version, ...changes }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier</DialogTitle><DialogDescription>{item.number}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-edit-title">Titre *</Label>
            <Input id="product-edit-title" value={draft.title || ""} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} maxLength={140} data-testid="product-edit-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="product-edit-description">Description</Label>
            <Textarea id="product-edit-description" value={draft.description || ""} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} rows={5} maxLength={4000} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField id="product-edit-type" label="Type" value={draft.type} onChange={(v) => setDraft((d) => ({ ...d, type: v }))} options={TYPE_LABELS} />
            <SelectField id="product-edit-source" label="Source" value={draft.source} onChange={(v) => setDraft((d) => ({ ...d, source: v }))} options={SOURCE_LABELS} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="product-edit-estimate">Pharmacies concernées (estimation)</Label>
            <Input id="product-edit-estimate" type="number" min="0" inputMode="numeric" value={draft.estimate} onChange={(e) => setDraft((d) => ({ ...d, estimate: e.target.value }))}
              className="max-w-[10rem]" />
            <p className="text-xs text-slate-500">À remplir seulement quand aucun ticket ne couvre le cas ; les tickets liés sont comptés à part.</p>
          </div>
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!valid || Object.keys(changes).length === 0}
          label="Enregistrer" testid="product-edit-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Merge (D-14) -----------------------------------------------------------------------------------------------------------------
export function MergeDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [master, setMaster] = useState(null);
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setMaster(null); setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const submit = () => req.run(() => api.post(`/product/items/${item.id}/merge`,
    { expected_version: item.version, master_id: master.id, ...(reason.trim() ? { reason: reason.trim() } : {}) }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fusionner dans un autre retour</DialogTitle>
          <DialogDescription>{item.number} suivra l'élément choisi. Ses tickets et ses pharmacies seront comptés avec lui.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {master ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="product-merge-master">
              <span className="min-w-0 break-words"><span className="text-xs text-slate-500 tabular-nums">{master.number}</span> {master.title}</span>
              <button type="button" aria-label="Choisir un autre élément" onClick={() => setMaster(null)}><X size={14} /></button>
            </div>
          ) : (
            <ItemPicker excludeIds={[item.id]} onPick={setMaster} placeholder="Retour à conserver (numéro ou titre)…" testid="product-merge-picker" />
          )}
          <div className="space-y-1.5">
            <Label htmlFor="product-merge-reason">Motif (facultatif)</Label>
            <Input id="product-merge-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Même problème" />
          </div>
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!master} label="Fusionner" testid="product-merge-submit" />
      </DialogContent>
    </Dialog>
  );
}

export function UnmergeDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const submit = () => req.run(() => api.post(`/product/items/${item.id}/unmerge`, { expected_version: item.version, reason: reason.trim() }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Défaire la fusion</DialogTitle>
          <DialogDescription>{item.number} redevient un retour à part entière.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="product-unmerge-reason">Motif *</Label>
          <Textarea id="product-unmerge-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="product-unmerge-reason" />
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={reason.trim().length < 3}
          label="Défaire la fusion" testid="product-unmerge-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Links ------------------------------------------------------------------------------------------------------------------------
export function TicketLinkDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  useEffect(() => { if (open) req.setError(""); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const pick = (ticket) => req.run(() => api.post(`/product/items/${item.id}/tickets`, { ticket_id: ticket.id }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rattacher un ticket</DialogTitle>
          <DialogDescription>Seuls les tickets que vous pouvez consulter sont proposés. Le texte du ticket n'est pas recopié.</DialogDescription>
        </DialogHeader>
        <TicketPicker excludeIds={(item.tickets || []).map((t) => t.id)} onPick={pick} testid="product-ticket-picker" />
        {req.busy && <p className="text-xs text-slate-500">Rattachement…</p>}
        {req.error && <p className="text-sm text-red-600" role="alert" data-testid="product-dialog-error">{req.error}</p>}
      </DialogContent>
    </Dialog>
  );
}

// `basePath` lets a release use the same dialog (`/product/releases/{id}/github`) as an item.
export function GithubDialog({ item, open, onOpenChange, onDone, basePath = "/product/items" }) {
  const req = useRequest();
  const [url, setUrl] = useState("");
  const [state, setState] = useState(NONE);
  useEffect(() => { if (open) { setUrl(""); setState(NONE); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const submit = () => req.run(() => api.post(`${basePath}/${item.id}/github`, { url: url.trim(), ...(state !== NONE ? { state } : {}) }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter un lien GitHub</DialogTitle>
          <DialogDescription>Une simple adresse : le CRM ne contacte jamais GitHub et l'état d'une issue ne change jamais celui du retour.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-github-url">Adresse *</Label>
            <Input id="product-github-url" value={url} onChange={(e) => setUrl(e.target.value)} maxLength={300} inputMode="url" data-testid="product-github-url"
              placeholder="https://github.com/…/issues/12" />
            <p className="text-xs text-slate-500">Issue, pull request, commit, release ou dépôt.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="product-github-state">État affiché (facultatif)</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger id="product-github-state" data-testid="product-github-state"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Non précisé</SelectItem>
                {Object.entries(GITHUB_STATE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={url.trim().length < 10} label="Ajouter" testid="product-github-submit" />
      </DialogContent>
    </Dialog>
  );
}
